// client/src/components/reports/ReportPermissions.jsx
//
// Who can do what in Reports, per role (owner always can everything). Shown
// to users with the "manage" permission. Server: PUT /api/reports/permissions.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveReportPermissions, errorText } from '../../services/reportsApi';

export default function ReportPermissions({ config, onSaved }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(config.permissions);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const roleLabel = {
    admin: t('reports.perms.admin', 'Admin'),
    manager: t('reports.perms.manager', 'Manager'),
    operator: t('reports.perms.operator', 'Operator'),
    contributor: t('reports.perms.contributor', 'Contributor'),
  };
  const actionLabel = {
    create: t('reports.perms.create', 'Build, edit and delete reports'),
    schedule: t('reports.perms.schedule', 'Schedule reports'),
    export: t('reports.perms.export', 'Export CSV / PDF'),
    view_shared: t('reports.perms.view_shared', 'View and run shared reports'),
    view_pii: t('reports.perms.view_pii', 'See unmasked email, phone and address'),
    manage: t('reports.perms.manage', 'Change these permissions'),
  };

  const toggle = (action, role) => setDraft((d) => ({
    ...d,
    [action]: d[action].includes(role) ? d[action].filter((r) => r !== role) : [...d[action], role],
  }));

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const res = await saveReportPermissions(draft);
      setDraft(res.permissions);
      setNotice({ kind: 'ok', text: t('reports.perms.saved', 'Permissions saved.') });
      if (onSaved) onSaved(res.permissions);
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('reports.perms.saveError', 'Could not save the permissions.')) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="surface" aria-labelledby="rp-perms-title">
      <h4 id="rp-perms-title">{t('reports.perms.title', 'Report permissions')}</h4>
      <p className="surface-muted perf-small">
        {t('reports.perms.intro', 'Choose what each role can do in Reports. The account owner can always do everything.')}
      </p>
      <div className="perf-table-wrap">
        <table className="perf-table rp-perm-table">
          <thead>
            <tr>
              <th scope="col">{t('reports.perms.action', 'Permission')}</th>
              <th scope="col">{t('reports.perms.owner', 'Owner')}</th>
              {config.roles.map((r) => <th key={r} scope="col">{roleLabel[r] || r}</th>)}
            </tr>
          </thead>
          <tbody>
            {config.actions.map((a) => (
              <tr key={a.key}>
                <th scope="row" style={{ fontWeight: 400 }}>{actionLabel[a.key] || a.label}</th>
                <td data-role={t('reports.perms.owner', 'Owner')}><input type="checkbox" checked disabled aria-label={`${actionLabel[a.key] || a.label}: ${t('reports.perms.owner', 'Owner')}`} /></td>
                {config.roles.map((r) => (
                  <td key={r} data-role={roleLabel[r] || r}>
                    <input
                      type="checkbox"
                      checked={(draft[a.key] || []).includes(r)}
                      onChange={() => toggle(a.key, r)}
                      aria-label={`${actionLabel[a.key] || a.label}: ${roleLabel[r] || r}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rp-actions" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
        <button type="button" className="lm-btn secondary" onClick={() => setDraft(config.defaults)}>
          {t('reports.perms.reset', 'Reset to defaults')}
        </button>
        <button type="button" className="lm-btn" onClick={save} disabled={saving}>
          {t('reports.perms.save', 'Save permissions')}
        </button>
      </div>
      {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
    </section>
  );
}
